import api from './client';
import { ChickRearMethod, Gender, GenderDeterminationMethod } from '../utils/enums';

export interface ChickInfo {
  id: string;
  eggId: string;
  hatchDate: Date;
  ageInDays: number;
  hatchWeight?: number;
  currentWeight?: number;
  rearMethod: ChickRearMethod;
  cageNumber: string;
  notes?: string;
  healthStatus?: string;
  bandingDate?: Date;
  bandNumber?: string;
  isBanded: boolean;
  /** Egg lifecycle status while on the chicks list (HATCHED or FLEDGED). */
  status?: string;
  
  // Breeding information
  clutchId: string;
  clutchDate: Date;
  breedingPairId: string;
  
  // Parent information
  maleParent?: {
    id: string;
    bandId: string;
    species?: string;
  };
  femaleParent?: {
    id: string;
    bandId: string;
    species?: string;
  };
  
  // Species information
  species?: {
    id: string;
    commonName: string;
    genus: string;
    species: string;
    scientificName: string;
  };
  
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateChickDto {
  eggId: string;
  hatchDate: string;
  hatchWeight?: number;
  rearMethod: ChickRearMethod;
  cageNumber: string;
  notes?: string;
  healthStatus?: string;
  currentWeight?: number;
}

export interface UpdateChickDto {
  hatchDate?: string;
  hatchWeight?: number;
  rearMethod?: ChickRearMethod;
  cageNumber?: string;
  notes?: string;
  healthStatus?: string;
  currentWeight?: number;
  bandingDate?: string;
  bandNumber?: string;
  ageInDays?: number;
}

export interface BandChickDto {
  bandId: string;
  gender: Gender;
  genderDeterminationMethod: GenderDeterminationMethod;
  bandingDate: string;
  cageNumber: string;
  mutationName?: string;
  phenotype?: string;
  genotype?: string;
  notes?: string;
  sellPrice?: number;
  genotypes?: Array<{ locusId: string; alleles: string[]; notes?: string }>;
}

export interface ChickStatistics {
  totalChicks: number;
  unbandedChicks: number;
  bandedChicks: number;
  averageAgeInDays: number;
  byRearMethod: Record<string, number>;
  bySpecies: Record<string, number>;
  recentHatches: number;
}

// Get all chicks
export const getChicks = async (): Promise<ChickInfo[]> => {
  try {
    const response = await api.get('chicks');
    const chicksData = response.data as any[];
    return chicksData.map((chick: any) => ({
      ...chick,
      hatchDate: new Date(chick.hatchDate),
      clutchDate: new Date(chick.clutchDate),
      bandingDate: chick.bandingDate ? new Date(chick.bandingDate) : undefined,
      createdAt: new Date(chick.createdAt),
      updatedAt: new Date(chick.updatedAt),
    }));
  } catch (error) {
    console.error('Error fetching chicks:', error);
    throw error;
  }
};

// Get unbanded chicks
export const getUnbandedChicks = async (): Promise<ChickInfo[]> => {
  try {
    const response = await api.get('chicks/unbanded');
    const chicksData = response.data as any[];
    return chicksData.map((chick: any) => ({
      ...chick,
      hatchDate: new Date(chick.hatchDate),
      clutchDate: new Date(chick.clutchDate),
      bandingDate: chick.bandingDate ? new Date(chick.bandingDate) : undefined,
      createdAt: new Date(chick.createdAt),
      updatedAt: new Date(chick.updatedAt),
    }));
  } catch (error) {
    console.error('Error fetching unbanded chicks:', error);
    throw error;
  }
};

// Get banded chicks
export const getBandedChicks = async (): Promise<ChickInfo[]> => {
  try {
    const response = await api.get('chicks/banded');
    const chicksData = response.data as any[];
    return chicksData.map((chick: any) => ({
      ...chick,
      hatchDate: new Date(chick.hatchDate),
      clutchDate: new Date(chick.clutchDate),
      bandingDate: chick.bandingDate ? new Date(chick.bandingDate) : undefined,
      createdAt: new Date(chick.createdAt),
      updatedAt: new Date(chick.updatedAt),
    }));
  } catch (error) {
    console.error('Error fetching banded chicks:', error);
    throw error;
  }
};

// Get chick by ID
export const getChick = async (id: string): Promise<ChickInfo> => {
  try {
    const response = await api.get(`chicks/${id}`);
    const chick = response.data as any;
    return {
      ...chick,
      hatchDate: new Date(chick.hatchDate),
      clutchDate: new Date(chick.clutchDate),
      bandingDate: chick.bandingDate ? new Date(chick.bandingDate) : undefined,
      createdAt: new Date(chick.createdAt),
      updatedAt: new Date(chick.updatedAt),
    };
  } catch (error) {
    console.error('Error fetching chick:', error);
    throw error;
  }
};

// Get chicks by breeding pair
export const getChicksByBreedingPair = async (breedingPairId: string): Promise<ChickInfo[]> => {
  try {
    const response = await api.get(`chicks/breeding-pair/${breedingPairId}`);
    const chicksData = response.data as any[];
    return chicksData.map((chick: any) => ({
      ...chick,
      hatchDate: new Date(chick.hatchDate),
      clutchDate: new Date(chick.clutchDate),
      bandingDate: chick.bandingDate ? new Date(chick.bandingDate) : undefined,
      createdAt: new Date(chick.createdAt),
      updatedAt: new Date(chick.updatedAt),
    }));
  } catch (error) {
    console.error('Error fetching chicks by breeding pair:', error);
    throw error;
  }
};

// Get chicks by clutch
export const getChicksByClutch = async (clutchId: string): Promise<ChickInfo[]> => {
  try {
    const response = await api.get(`chicks/clutch/${clutchId}`);
    const chicksData = response.data as any[];
    return chicksData.map((chick: any) => ({
      ...chick,
      hatchDate: new Date(chick.hatchDate),
      clutchDate: new Date(chick.clutchDate),
      bandingDate: chick.bandingDate ? new Date(chick.bandingDate) : undefined,
      createdAt: new Date(chick.createdAt),
      updatedAt: new Date(chick.updatedAt),
    }));
  } catch (error) {
    console.error('Error fetching chicks by clutch:', error);
    throw error;
  }
};

// Create a chick
export const createChick = async (data: CreateChickDto): Promise<ChickInfo> => {
  try {
    const response = await api.post('chicks', data);
    const chick = response.data as any;
    return {
      ...chick,
      hatchDate: new Date(chick.hatchDate),
      clutchDate: new Date(chick.clutchDate),
      bandingDate: chick.bandingDate ? new Date(chick.bandingDate) : undefined,
      createdAt: new Date(chick.createdAt),
      updatedAt: new Date(chick.updatedAt),
    };
  } catch (error) {
    console.error('Error creating chick:', error);
    throw error;
  }
};

// Update a chick
export const updateChick = async (id: string, data: UpdateChickDto): Promise<ChickInfo> => {
  try {
    const response = await api.patch(`chicks/${id}`, data);
    const chick = response.data as any;
    return {
      ...chick,
      hatchDate: new Date(chick.hatchDate),
      clutchDate: new Date(chick.clutchDate),
      bandingDate: chick.bandingDate ? new Date(chick.bandingDate) : undefined,
      createdAt: new Date(chick.createdAt),
      updatedAt: new Date(chick.updatedAt),
    };
  } catch (error) {
    console.error('Error updating chick:', error);
    throw error;
  }
};

// Delete a chick
export const deleteChick = async (id: string): Promise<void> => {
  try {
    await api.delete(`chicks/${id}`);
  } catch (error) {
    console.error('Error deleting chick:', error);
    throw error;
  }
};

// Band a chick
export const bandChick = async (id: string, data: BandChickDto): Promise<any> => {
  try {
    const response = await api.post(`chicks/${id}/band`, data);
    return response.data;
  } catch (error) {
    console.error('Error banding chick:', error);
    throw error;
  }
};

// Get chick statistics
export const getChickStatistics = async (): Promise<ChickStatistics> => {
  try {
    const response = await api.get('chicks/statistics');
    return response.data as ChickStatistics;
  } catch (error) {
    console.error('Error fetching chick statistics:', error);
    throw error;
  }
}; 